import { Fragment, useMemo } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  Bar,
  BarChart,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import PsychologyIcon from '@mui/icons-material/Psychology';
import MoodIcon from '@mui/icons-material/Mood';
import PanToolAltIcon from '@mui/icons-material/PanToolAlt';
import ScheduleIcon from '@mui/icons-material/Schedule';

import type { CognitiveScore } from '../api/cognitive';
import type { MasterBaseline } from '../api/masterBaselines';
import { SearchableSelect } from './SearchableSelect';
import type { MotionScore } from '../api/motion';

/**
 * Participant performance table — ROADMAP #43.1 phase 1B participant
 * surface.
 *
 * Layout: a single table with one row per metric (Duration, Average
 * Focus, Average Emotional Engagement, Bimanual Coactivity) and one
 * column per procedure the participant has captured. Procedure name
 * appears once in the column header rather than repeated on every
 * metric card.
 *
 *     ┌──────────────┬──────────┬──────────┬──────────┐
 *     │              │ Lap Chole│ Knee Art │ Cardiac  │
 *     ├──────────────┼──────────┼──────────┼──────────┤
 *     │ Duration     │  [chart] │  [chart] │  [chart] │
 *     ├──────────────┼──────────┼──────────┼──────────┤
 *     │ Avg Focus    │  [chart] │  [chart] │  [chart] │
 *     ├──────────────┼──────────┼──────────┼──────────┤
 *     │ Avg Engage   │  [chart] │  [chart] │  [chart] │
 *     ├──────────────┼──────────┼──────────┼──────────┤
 *     │ Bimanual     │  [chart] │  [chart] │  [chart] │
 *     └──────────────┴──────────┴──────────┴──────────┘
 *
 * Each cell renders a small grouped bar chart comparing the
 * participant's own value ("You") against a reference set
 * ("Faculty / Practice-ready" — ROADMAP #44 master baseline). The
 * Faculty bar is omitted when no master row matches the procedure's
 * group, so per-procedure context flips on automatically once the
 * group is set on a procedure.
 */

interface CapturedSession {
  session_id: string;
  procedure_name: string | null;
  procedure_group?: string | null;
  started_at: string;
  ended_at: string | null;
}

interface Props {
  loading: boolean;
  error: string | null;
  cognitiveScores: CognitiveScore[];
  motionScores: MotionScore[];
  sessions: CapturedSession[];
  /** ROADMAP #44 reference catalogue. Optional — when omitted /
   *  empty, the Faculty bars stay empty (same behaviour as the
   *  pre-#44 stub). */
  masterBaselines?: MasterBaseline[];
  /** "" = all procedures. Controlled by the parent so future
   *  sibling panels can share the filter. */
  procedureFilter: string;
  onProcedureFilterChange: (next: string) => void;
  /** Tenant `scorecard_hidden_metrics` keys. Rows whose metric is
   *  hidden are dropped so this grid matches the visibility the server
   *  already applies to the scorecard. Omit / empty = show all. */
  hiddenMetrics?: string[];
}

interface ProcedureBucket {
  procedureName: string;
  /** Maps to a master_baselines.procedure_group; null = no master
   *  mapping (Faculty bar stays empty). Source preference:
   *  capture_sessions.procedure_group (denormalised through the
   *  procedures join), then cognitive/motion rows. */
  procedureGroup: string | null;
  /** Mean Focus across this procedure's scored phases — null if no data. */
  meanFocus: number | null;
  /** Mean Emotional Engagement (= cognitive `engagement` index,
   *  β/(α+θ)) across this procedure's scored phases. */
  meanEngagement: number | null;
  /** Mean Duration in seconds across this procedure's sessions — null if no rows. */
  meanDurationS: number | null;
  /** Mean bimanual coactivity (gyro-based `bidex_v2`) across this procedure's scored
   *  phases — null if no data. The speed-based v1 is retired (IMU-DISTANCE-METRICS). */
  meanBimanual: number | null;
  /** Number of captured recordings (sessions) for this procedure. */
  sessionCount: number;
}

interface MetricConfig {
  key: string;
  title: string;
  icon: React.ReactNode;
  getValue: (b: ProcedureBucket) => number | null;
  /** Faculty/master comparator for this row — receives the same
   *  bucket so per-procedure lookups by procedure_group work. */
  getMaster: (
    b: ProcedureBucket,
    baselines: MasterBaseline[] | undefined,
  ) => number | null;
  thresholdLine?: { value: number; label: string };
  /** Defaults to integer formatting. Override for ratio metrics. */
  valueFormatter?: (v: number) => string;
}

/** Look up a single master value matching the filter, or null. */
function masterValue(
  baselines: MasterBaseline[] | undefined,
  params: {
    cohort: string;
    procedureGroup: string | null;
    metric: string;
    statistic: 'mean' | 'median' | 'min' | 'max';
  },
): number | null {
  if (!baselines || baselines.length === 0) return null;
  const hit = baselines.find(
    (b) =>
      b.cohort === params.cohort &&
      b.procedure_group === params.procedureGroup &&
      b.metric === params.metric &&
      b.statistic === params.statistic,
  );
  return hit ? hit.value : null;
}

function meanOf(values: Array<number | null>): number | null {
  const finite = values.filter(
    (v): v is number => v !== null && Number.isFinite(v),
  );
  if (finite.length === 0) return null;
  return finite.reduce((a, b) => a + b, 0) / finite.length;
}

function bucketByProcedure(
  cognitiveScores: CognitiveScore[],
  motionScores: MotionScore[],
  sessions: CapturedSession[],
): ProcedureBucket[] {
  // Union of procedure names — a column appears if any data source
  // (sessions, cognitive, or motion) mentioned it.
  const procNames = new Set<string>();
  for (const s of sessions) {
    if (s.procedure_name) procNames.add(s.procedure_name);
  }
  for (const c of cognitiveScores) {
    if (c.procedure_name) procNames.add(c.procedure_name);
  }
  for (const m of motionScores) {
    if (m.procedure_name) procNames.add(m.procedure_name);
  }

  const result: ProcedureBucket[] = [];
  for (const name of procNames) {
    const cogForProc = cognitiveScores.filter(
      (c) => c.procedure_name === name,
    );
    const motForProc = motionScores.filter((m) => m.procedure_name === name);
    const sessForProc = sessions.filter((s) => s.procedure_name === name);
    // First non-null procedure_group across the three sources wins.
    // Sessions list isn't always backfilled with the group, so the
    // cognitive/motion rows are checked too.
    const groupFromSession = sessForProc
      .map((s) => s.procedure_group ?? null)
      .find((g): g is string => !!g);
    const groupFromCog = cogForProc
      .map((c) => c.procedure_group ?? null)
      .find((g): g is string => !!g);
    const groupFromMot = motForProc
      .map((m) => m.procedure_group ?? null)
      .find((g): g is string => !!g);
    const procedureGroup =
      groupFromSession ?? groupFromCog ?? groupFromMot ?? null;
    const durations = sessForProc
      .map((s) => {
        if (!s.started_at || !s.ended_at) return null;
        const ms = new Date(s.ended_at).getTime() - new Date(s.started_at).getTime();
        return ms > 0 ? ms / 1000 : null;
      })
      .filter((d): d is number => d !== null);
    result.push({
      procedureName: name,
      procedureGroup,
      meanFocus: meanOf(cogForProc.map((c) => c.focus)),
      meanEngagement: meanOf(cogForProc.map((c) => c.engagement)),
      meanDurationS:
        durations.length > 0
          ? durations.reduce((a, b) => a + b, 0) / durations.length
          : null,
      meanBimanual: meanOf(motForProc.map((m) => m.bidex_v2 ?? null)),
      sessionCount: sessForProc.length,
    });
  }
  // Alphabetical so the column order is stable across loads.
  result.sort((a, b) => a.procedureName.localeCompare(b.procedureName));
  return result;
}

/**
 * Maps each grid row's `key` to the tenant `scorecard_hidden_metrics`
 * key (which mirrors `master_baselines.metric` / the cohort registry).
 * Only Bimanual differs; the rest are 1:1. A row is hidden when its
 * mapped key is in the tenant's hidden set.
 */
const METRIC_KEY_TO_HIDDEN: Record<string, string> = {
  duration: 'duration',
  focus: 'focus',
  engagement: 'engagement',
  bimanual: 'bidex_v2',
};

/** Each row of the table. Order is fixed; columns are dynamic. */
const METRICS: MetricConfig[] = [
  {
    key: 'duration',
    title: 'Duration (seconds)',
    icon: <ScheduleIcon />,
    getValue: (b) => b.meanDurationS,
    getMaster: (b, baselines) =>
      masterValue(baselines, {
        cohort: 'practice_ready',
        procedureGroup: b.procedureGroup,
        metric: 'duration',
        statistic: 'median',
      }),
  },
  {
    key: 'focus',
    title: 'Average Focus',
    icon: <PsychologyIcon />,
    getValue: (b) => b.meanFocus,
    getMaster: (_, baselines) =>
      // Focus is not procedure-specific — single attending row.
      masterValue(baselines, {
        cohort: 'attending',
        procedureGroup: null,
        metric: 'focus',
        statistic: 'mean',
      }),
    thresholdLine: { value: 1, label: 'β/α = 1' },
    valueFormatter: (v) => v.toFixed(2),
  },
  {
    key: 'engagement',
    title: 'Average Emotional Engagement',
    icon: <MoodIcon />,
    getValue: (b) => b.meanEngagement,
    getMaster: (_, baselines) =>
      // Engagement reference is not procedure-specific — single
      // attending row with procedure_group=NULL.
      masterValue(baselines, {
        cohort: 'attending',
        procedureGroup: null,
        metric: 'engagement',
        statistic: 'mean',
      }),
    valueFormatter: (v) => v.toFixed(2),
  },
  {
    key: 'bimanual',
    title: 'Bimanual Coactivity',
    icon: <PanToolAltIcon />,
    getValue: (b) => b.meanBimanual,
    getMaster: (b, baselines) =>
      masterValue(baselines, {
        cohort: 'practice_ready',
        procedureGroup: b.procedureGroup,
        metric: 'bidex_v2',
        statistic: 'median',
      }),
    valueFormatter: (v) => v.toFixed(2),
  },
];

const ALL_PROCEDURES = [{ value: '', label: 'All procedures' }];

export function CognitivePerformanceGrid(props: Props) {
  // Available procedures are derived from the sessions roster (which
  // is always tenant-/me-scoped and unfiltered server-side) so the
  // dropdown stays stable while the cognitive feed narrows in / out
  // when the parent applies a backend procedure filter.
  const availableProcedures = useMemo(() => {
    const names = new Set<string>();
    for (const s of props.sessions) {
      if (s.procedure_name) names.add(s.procedure_name);
    }
    return Array.from(names).sort();
  }, [props.sessions]);
  const procedureOptions = useMemo(
    () => availableProcedures.map((p) => ({ value: p, label: p })),
    [availableProcedures],
  );

  // Sessions aren't refetched on filter change (one tenant-wide
  // call); narrow them client-side. The cognitive + motion feeds
  // are already backend-narrowed when the filter is set.
  const filteredSessions = useMemo(() => {
    if (!props.procedureFilter) return props.sessions;
    return props.sessions.filter(
      (s) => s.procedure_name === props.procedureFilter,
    );
  }, [props.sessions, props.procedureFilter]);

  const buckets = useMemo(
    () =>
      bucketByProcedure(
        props.cognitiveScores,
        props.motionScores,
        filteredSessions,
      ),
    [props.cognitiveScores, props.motionScores, filteredSessions],
  );

  // Drop rows the tenant admin has hidden, so the "Your performance"
  // grid honours the same visibility the server applies to the
  // scorecard (ROADMAP participant-visibility feedback).
  const visibleMetrics = useMemo(() => {
    const hidden = new Set(props.hiddenMetrics ?? []);
    return METRICS.filter(
      (m) => !hidden.has(METRIC_KEY_TO_HIDDEN[m.key] ?? m.key),
    );
  }, [props.hiddenMetrics]);

  if (props.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (props.error) {
    return <Alert severity="error">{props.error}</Alert>;
  }
  if (availableProcedures.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No performance data yet. Once your capture-app sessions are
          scored, your trait performance will surface here as a
          per-procedure breakdown.
        </Typography>
      </Paper>
    );
  }

  // First column is the metric-label column; one extra column per
  // procedure. `minmax(120px, 1fr)` lets columns shrink toward the
  // chart's intrinsic 120px floor when there are many procedures,
  // then evenly distribute remaining space across them.
  const gridTemplateColumns = `220px repeat(${Math.max(buckets.length, 1)}, minmax(120px, 1fr))`;

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <LegendSwatch color="#1e3a8a" label="You" />
          <LegendSwatch color="#94a3b8" label="Practice-ready" />
        </Stack>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography variant="caption" color="text.secondary">
            Per-procedure breakdown of your captured sessions
          </Typography>
          <SearchableSelect
            id="participant-procedure"
            label="Procedure"
            size="small"
            sx={{ minWidth: 200 }}
            value={props.procedureFilter}
            onChange={props.onProcedureFilterChange}
            pinned={ALL_PROCEDURES}
            options={procedureOptions}
          />
        </Stack>
      </Stack>

      <Card>
        <CardContent>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns,
              columnGap: 2,
              rowGap: 2,
              alignItems: 'stretch',
            }}
          >
            {/* Top-left corner is empty so the procedure-name row
                doesn't collide with the metric-label column. */}
            <Box />
            {buckets.map((b) => (
              <ProcedureHeader
                key={b.procedureName}
                name={b.procedureName}
                sessionCount={b.sessionCount}
              />
            ))}

            {/* One row per metric. Fragment keeps the grid-layout
                children flat so auto-placement flows them
                left-to-right and wraps at the column count. */}
            {visibleMetrics.map((metric) => (
              <Fragment key={metric.key}>
                <MetricLabel icon={metric.icon} title={metric.title} />
                {buckets.map((b) => (
                  <MetricCell
                    key={b.procedureName}
                    bucket={b}
                    value={metric.getValue(b)}
                    masterReference={metric.getMaster(b, props.masterBaselines)}
                    thresholdLine={metric.thresholdLine}
                    valueFormatter={metric.valueFormatter}
                  />
                ))}
              </Fragment>
            ))}
          </Box>
        </CardContent>
      </Card>
    </Stack>
  );
}

// ----------------------------------------------------------------------
// Internals
// ----------------------------------------------------------------------

function LegendSwatch({ color, label }: { color: string; label: string }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Box
        sx={{ width: 14, height: 14, bgcolor: color, borderRadius: '2px' }}
      />
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );
}

function ProcedureHeader({
  name,
  sessionCount,
}: {
  name: string;
  sessionCount: number;
}) {
  return (
    <Tooltip title={name} placement="top">
      <Box
        sx={{
          textAlign: 'center',
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="subtitle2"
          fontWeight={600}
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {name}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block' }}
        >
          {sessionCount} recording{sessionCount === 1 ? '' : 's'}
        </Typography>
      </Box>
    </Tooltip>
  );
}

function MetricLabel({
  icon,
  title,
}: {
  icon: React.ReactNode;
  title: string;
}) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        pr: 2,
        borderRight: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ color: 'primary.main' }}>{icon}</Box>
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
    </Stack>
  );
}

function MetricCell({
  bucket,
  value,
  masterReference,
  thresholdLine,
  valueFormatter,
}: {
  bucket: ProcedureBucket;
  value: number | null;
  /** ROADMAP #44 Faculty / experienced-cohort comparator. When set,
   *  renders alongside the You bar; the caption shows the absolute
   *  delta. Null when no master row matches this procedure's group. */
  masterReference: number | null;
  thresholdLine?: { value: number; label: string };
  valueFormatter?: (v: number) => string;
}) {
  const hasMaster = masterReference !== null;
  const data = [
    {
      name: 'You',
      you: value ?? 0,
      faculty: hasMaster ? masterReference ?? 0 : null,
    },
  ];

  const formatLabel = valueFormatter ?? ((v: number) => v.toFixed(0));

  // Delta = your value − master. Higher / lower direction is metric-
  // specific (e.g. higher Focus = better, lower Duration = better),
  // so we show the signed delta and leave the operator to interpret.
  let deltaCaption: string | null = null;
  if (hasMaster && value !== null && Number.isFinite(value)) {
    const delta = value - (masterReference ?? 0);
    const sign = delta >= 0 ? '+' : '−';
    deltaCaption = `Δ ${sign}${formatLabel(Math.abs(delta))} vs practice-ready`;
  } else if (hasMaster && value === null) {
    deltaCaption = '(no data yet)';
  }

  return (
    // Unique aria-label per cell so a screen reader can announce
    // which procedure / metric it's reading.
    <Stack
      spacing={0.5}
      aria-label={`${bucket.procedureName}: ${
        value !== null ? formatLabel(value) : 'no data'
      }`}
    >
      <Box sx={{ height: 90 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 16, right: 4, left: 4, bottom: 4 }}>
            <XAxis hide dataKey="name" />
            <YAxis hide domain={[0, 'auto']} />
            {thresholdLine && (
              <ReferenceLine
                y={thresholdLine.value}
                stroke="#16a34a"
                strokeDasharray="3 3"
              />
            )}
            <Bar
              dataKey="you"
              radius={[4, 4, 0, 0]}
              label={({ x, y, width, value: v }) => {
                if (v === null || v === undefined) return <g />;
                return (
                  <text
                    x={(x as number) + (width as number) / 2}
                    y={(y as number) - 4}
                    fontSize={11}
                    fill="#1e3a8a"
                    textAnchor="middle"
                  >
                    {formatLabel(v as number)}
                  </text>
                );
              }}
            >
              {data.map((_, i) => (
                <Cell
                  key={i}
                  fill={value === null ? '#cbd5e1' : '#1e3a8a'}
                />
              ))}
            </Bar>
            {hasMaster && (
              <Bar
                dataKey="faculty"
                radius={[4, 4, 0, 0]}
                label={({ x, y, width, value: v }) => {
                  if (v === null || v === undefined) return <g />;
                  return (
                    <text
                      x={(x as number) + (width as number) / 2}
                      y={(y as number) - 4}
                      fontSize={11}
                      fill="#475569"
                      textAnchor="middle"
                    >
                      {formatLabel(v as number)}
                    </text>
                  );
                }}
              >
                {data.map((_, i) => (
                  <Cell key={`fac-${i}`} fill="#94a3b8" />
                ))}
              </Bar>
            )}
          </BarChart>
        </ResponsiveContainer>
      </Box>
      {deltaCaption && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textAlign: 'center' }}
        >
          {deltaCaption}
        </Typography>
      )}
    </Stack>
  );
}
